// @module Ames.RentFromAnywhere.RentFromAnywhere
// Rental orders are credit-card only: when the cart contains rental lines,
// hide every other payment option in checkout (and PayPal express buttons),
// force the credit card selection, and block submit as a safety net.
define("Ames.RentFromAnywhere.PaymentRestriction", [
  "LiveOrder.Model",
  "Ames.RentFromAnywhere.RentalLine",
  "jQuery",
  "underscore"
], function (LiveOrderModel, RentalLine, jQuery, _) {
  "use strict";

  var MESSAGE = "Rental orders can only be paid with a credit card. The card used at checkout is charged automatically each month while tools are on rent.";

  // Payment methods allowed for rental orders. Selector button values
  // (creditcard / paypal / others) and LiveOrder paymentmethod types
  // (creditcard / paypal / invoice / giftcertificate ...) are both matched here.
  var ALLOWED = ["creditcard", "invoice"];

  // Anything matching these is hidden when the cart has rental lines.
  // Add selectors here if a new payment integration shows up.
  var HIDE_SELECTORS = [
    '[data-view="ExternalPayment.PayPal"]',
    '[data-view="ExternalPayment"]',
    '[data-view="ExternalPayment.Option"]',
    '.cart-summary-paypal',
    '.paypal-button',
    '[data-action="paypal"]',
    '.payment-wizard-paypal',
    '.order-wizard-paypal',
    '.affirm-as-low-as',
    '[data-view="Affirm.Promo"]'
  ].join(",");

  // Text that describes ONE option: for radios the label next to it, for the
  // theme's <a> buttons the button's own text (never a shared parent, which
  // would contain every option's label).
  function optionText($el) {
    if ($el.is("input")) {
      var id = $el.attr("id");
      var $label = id ? jQuery('label[for="' + id + '"]') : jQuery();
      if (!$label.length) $label = $el.closest("label");
      return ($label.text() || "").toLowerCase();
    }
    return ($el.text() || "").toLowerCase();
  }

  function optionValue($el) {
    var v = $el.attr("value");
    if (v === undefined && $el.is("input")) v = $el.val();
    return (v || "").toString().toLowerCase();
  }

  function isCreditCardOption($el) {
    var value = optionValue($el);
    if (value) return value === "creditcard";
    return /credit|debit|card/.test(optionText($el));
  }

  function isAllowedOption($el) {
    var value = optionValue($el);
    if (value) return _.contains(ALLOWED, value);
    var text = optionText($el);
    if (_.contains(ALLOWED, "creditcard") && /credit|debit|card/.test(text)) return true;
    if (_.contains(ALLOWED, "invoice") && /invoice|terms|purchase order/.test(text)) return true;
    return false;
  }

  // Strict rule (shared): custcol_custom_rate or "N Days Rental" - configurable
  // sets also carry custcol_custom_price / description and must NOT match.
  function hasRentalLines(liveOrder) {
    return RentalLine.cartHasRentalLines(liveOrder);
  }

  function primaryPaymentType(liveOrder) {
    var methods = liveOrder.get("paymentmethods");
    if (!methods) return null;
    var models = methods.models || methods;
    var primary = _.find(models, function (m) {
      var get = _.isFunction(m.get) ? function (k) { return m.get(k); } : function (k) { return m[k]; };
      return get("primary") !== false && get("type") !== "giftcertificate";
    });
    if (!primary) return null;
    return _.isFunction(primary.get) ? primary.get("type") : primary.type;
  }

  function applyRestriction(liveOrder) {
    try {
      var rental = hasRentalLines(liveOrder);
      jQuery("body").toggleClass("rfa-rental-checkout", rental);
      if (!rental) return;

      // Payment selector: the theme renders <a value="creditcard|paypal|others">
      // buttons; other themes use radios. Handle both.
      var $options = jQuery(
        '.order-wizard-paymentmethod-selector-module-button, ' +
        '[data-action="change-payment-method"] a, ' +
        'input[type="radio"][name*="payment"], ' +
        '.payment-wizard-payment-selector-module input[type="radio"], ' +
        '[data-type="payment-selector"] input[type="radio"]'
      );
      if (!$options.length) return;

      var $cc = null;
      var anyAllowedSelected = false;
      $options.each(function () {
        var $o = jQuery(this);
        if (isAllowedOption($o)) {
          if (!$cc && isCreditCardOption($o)) $cc = $o;
          var sel = $o.is("input") ? $o.prop("checked") : $o.hasClass("selected");
          if (sel) anyAllowedSelected = true;
          return;
        }
        var $wrap = $o.is("input")
          ? $o.closest("label, li, .payment-wizard-payment-selector-module-option, .payment-selector-option").first()
          : $o;
        // inline too, so it never depends on the stylesheet being compiled
        $wrap.addClass("rfa-hidden-payment-option").css({ display: "none", pointerEvents: "none" });
      });

      // Only force credit card when the current selection is a hidden option
      if ($cc && !anyAllowedSelected) {
        var isSelected = $cc.is("input") ? $cc.prop("checked") : $cc.hasClass("selected");
        if (!isSelected) {
          if ($cc.is("input")) {
            $cc.prop("checked", true).trigger("click").trigger("change");
          } else {
            $cc.trigger("click");
          }
        }
      }

      var $radios = $options;
      var $container = $radios.first().closest(
        ".order-wizard-paymentmethod-selector-module, .payment-wizard-payment-selector-module, [data-type=\"payment-selector\"], .payment-wizard-module"
      );
      if ($container.length && !$container.find(".rfa-rental-payment-notice").length) {
        $container.prepend('<div class="rfa-rental-payment-notice">' + MESSAGE + "</div>");
      }
    } catch (e) {
      console.warn("RentFromAnywhere: payment restriction failed", e);
    }
  }

  return {
    mountToApp: function (container) {
      var liveOrder = LiveOrderModel.getInstance();
      var checkout = container.getComponent("Checkout");
      var layout = container.getComponent("Layout");

      var run = _.debounce(function () { applyRestriction(liveOrder); }, 150);

      // Re-apply whenever the layout or the checkout wizard re-renders
      if (layout && _.isFunction(layout.on)) {
        layout.on("afterAppendView", run);
        layout.on("afterRender", run);
      }
      liveOrder.on("change sync", run);

      // Cheap fallback for step/module re-renders that emit no event
      if (window.MutationObserver) {
        var target = document.getElementById("main-container") || document.body;
        new MutationObserver(run).observe(target, { childList: true, subtree: true });
      }

      run();

      // Safety net: refuse to submit a rental order with a non credit card payment
      if (checkout) {
        liveOrder.cancelableOn("before:LiveOrder.submit", function () {
          var defer = jQuery.Deferred();
          try {
            if (hasRentalLines(liveOrder)) {
              var type = primaryPaymentType(liveOrder);
              if (type && !_.contains(ALLOWED, String(type).toLowerCase())) {
                defer.reject({
                  errorCode: "ERR_RENTAL_PAYMENT_METHOD",
                  errorMessage: MESSAGE
                });
                return defer.promise();
              }
            }
          } catch (e) {
            console.warn("RentFromAnywhere: payment check failed, allowing submit", e);
          }
          defer.resolve();
          return defer.promise();
        });
      }
    }
  };
});
